// =============================================================================
// src/views/monitoramento/monitoramento-controller.ts — Comportamento da Tela 09.
//
// Proveniencia: assets/unified-app.js Sections.monitoramento (linhas 2033-2115).
//   Porte FIEL de activate/_applyFilter/deactivate. Estado vivo: filter ('all').
//   Zonas pintadas com a cor partidaria de 2024 a 0.55 de opacidade. Marcadores ao
//   vivo filtrados por tipo; contadores e linhas da tabela sincronizados; insight
//   (innerHTML) atualizado quando #u-mon-insight existir.
//
// .u-live-dot: o legado roda um setInterval(900ms) que alterna a opacidade de
//   `.u-live-dot`. Esse elemento NAO existe no markup (nem no legado) — o timer
//   roda mas o querySelector devolve null, virando no-op. Replicado FIELMENTE
//   (timer + guard) para paridade byte-a-byte; o deactivate limpa o timer.
//
// Reset de estilo: o legado faz `MapController.currentZoneStyleFn = null` no
//   deactivate (campo privado, fora do contrato). Mesma extensao local de
//   src/views/mapa-eleitoral/index.ts.
//
// SRP: estado + listeners + estilos de mapa da secao. Markup no index.ts.
// =============================================================================

import type { PathOptions } from 'leaflet';
import type { MapController, ZonaKey, LiveMarker, LiveMarkerType } from '../../app/types';
import { qs, qsa } from '../../app/dom';
import { bindOnce } from '../../core/ui';
import { MANAUS_LIVE_MARKERS, MANAUS_ELECTORAL_BY_YEAR } from '../../data';

/** Filtro de eventos: 'all' ou um tipo de marcador ao vivo (data-filter). */
type MonitorFilter = 'all' | LiveMarkerType;

/** Intervalo do pulso mock da `.u-live-dot` (legado: 900ms). */
const LIVE_PULSE_INTERVAL_MS = 900;

/** Campo privado de estilo zerado no deactivate (fora do contrato publico). */
interface MapControllerZoneStyleState {
  currentZoneStyleFn: unknown;
}

/** Texto (HTML) do insight por filtro (_applyFilter.insights, 2094-2101). */
const MONITOR_INSIGHT_BY_FILTER: Record<MonitorFilter, string> = {
  all: 'Todos os eventos ativos estão visíveis. A prioridade operacional combina urnas defeituosas, filas longas e desinformação viral na Zona Norte/Oeste.',
  crit: 'Filtro crítico ativo: urnas defeituosas e filas longas exigem redistribuição de fiscais para Norte e Oeste nos próximos 20 minutos.',
  warn: 'Alertas de mídia concentrados em WhatsApp e redes locais. A resposta recomendada é contra-narrativa curta com prova visual de seção regular.',
  event: 'Eventos de campanha aparecem sem impacto negativo imediato. O ganho está em registrar presença territorial e alimentar o modo cinema depois.',
  poll: 'Boca de urna isolada na Zona Sul confirma tendência de Almeida, mas precisa ser cruzada com apuração real antes de virar comunicação pública.',
  sec: 'Ocorrências de segurança estão controladas, porém a Zona Leste deve ficar em observação por risco de contágio em escolas próximas.',
};

/**
 * Controlador da secao Monitoramento. Pinta as zonas com a cor partidaria 2024,
 * filtra os eventos ao vivo (mapa + tabela + contadores + insight) e mantem o
 * timer-mock de pulso. Espelha o objeto literal Sections.monitoramento.
 */
class MonitoramentoCommandController {
  private filter: MonitorFilter = 'all';
  private liveTimer: ReturnType<typeof setInterval> | null = null;

  /** Ativa a secao (replica activate, 2037-2062): estilo de zona, filtro, botoes, timer. */
  activate(mapCtrl: MapController): void {
    mapCtrl.setZoneStyles((zonaKey) => partyZoneStyle(zonaKey));
    this.applyFilter(this.filter, mapCtrl);
    this.bindFilterButtons(mapCtrl);
    this.startLivePulse();
  }

  /** Liga os botoes de filtro de evento (.u-event-filter) — 2049-2054. */
  private bindFilterButtons(mapCtrl: MapController): void {
    qsa('.u-event-filter').forEach((btn) => {
      bindOnce(btn as HTMLElement, 'click', 'monitor-filter', () => {
        this.filter = readFilter(btn);
        this.applyFilter(this.filter, mapCtrl);
      });
    });
  }

  /**
   * Aplica um filtro (porte de _applyFilter, 2064-2104): injeta os marcadores
   * filtrados, sincroniza botoes/linhas/contadores e atualiza o insight.
   */
  private applyFilter(filter: MonitorFilter, mapCtrl: MapController): void {
    /* v8 ignore next -- this.filter é sempre MonitorFilter válido ('all' default/readFilter); || 'all' defensivo */
    const activeFilter = filter || 'all';
    mapCtrl.addMarkerLayer('monitoramento', filterLiveMarkers(activeFilter));
    syncFilterButtons(activeFilter);
    const visibleRows = syncEventRows(activeFilter);
    updateCounters(activeFilter, visibleRows);
    updateInsight(activeFilter);
  }

  /** Inicia o pulso mock da `.u-live-dot` (no-op visual; elemento ausente) — 2057-2061. */
  private startLivePulse(): void {
    if (this.liveTimer) clearInterval(this.liveTimer);
    this.liveTimer = setInterval(() => {
      const dot = qs<HTMLElement>('.u-live-dot');
      if (dot) dot.style.opacity = dot.style.opacity === '0.2' ? '1' : '0.2';
    }, LIVE_PULSE_INTERVAL_MS);
  }

  /** Desativa a secao (replica deactivate, 2106-2115): remove marcadores, timer e estilo. */
  deactivate(mapCtrl: MapController): void {
    mapCtrl.removeMarkerLayer('monitoramento');
    (mapCtrl as MapController & MapControllerZoneStyleState).currentZoneStyleFn = null;
    if (this.liveTimer) clearInterval(this.liveTimer);
    this.liveTimer = null;
  }
}

/** Estilo de zona: cor do partido vencedor em 2024 a 0.55 de opacidade (2040-2042). */
function partyZoneStyle(zonaKey: ZonaKey): PathOptions {
  const fillColor = MANAUS_ELECTORAL_BY_YEAR['2024'][zonaKey][1];
  return { fillColor, fillOpacity: 0.55, weight: 0.8, color: 'rgba(255,255,255,0.5)', opacity: 1 };
}

/** Marcadores ao vivo do filtro corrente ('all' = todos) — 2066-2068. */
function filterLiveMarkers(activeFilter: MonitorFilter): readonly LiveMarker[] {
  return MANAUS_LIVE_MARKERS.filter((marker) => activeFilter === 'all' || marker.type === activeFilter);
}

/** Marca o botao de filtro ativo (.u-event-filter) — 2071-2073. */
function syncFilterButtons(activeFilter: MonitorFilter): void {
  qsa('.u-event-filter').forEach((btn) => {
    btn.classList.toggle('active', ((btn as HTMLElement).dataset['filter'] ?? 'all') === activeFilter);
  });
}

/** Esconde/mostra as linhas da tabela e devolve quantas ficaram visiveis (2075-2080). */
function syncEventRows(activeFilter: MonitorFilter): number {
  let visibleRows = 0;
  qsa('.u-live-event-row').forEach((row) => {
    const visible = activeFilter === 'all' || (row as HTMLElement).dataset['type'] === activeFilter;
    row.classList.toggle('is-hidden', !visible);
    if (visible) visibleRows += 1;
  });
  return visibleRows;
}

/** Atualiza os contadores de eventos ativos e criticos (2082-2090). */
function updateCounters(activeFilter: MonitorFilter, visibleRows: number): void {
  const activeEvents = qs('#u-mon-active-events');
  if (activeEvents) activeEvents.textContent = String(visibleRows);
  const critCount = qs('#u-mon-critical-count');
  if (!critCount) return;
  const critRows = qsa('.u-live-event-row').filter((row) => {
    const type = (row as HTMLElement).dataset['type'];
    return (activeFilter === 'all' || type === activeFilter) && type === 'crit';
  }).length;
  critCount.textContent = `${critRows}${critRows === 1 ? ' crítico' : ' críticos'}`;
}

/**
 * Atualiza o insight via innerHTML (#u-mon-insight). O elemento nao existe no
 * markup (nem no legado): o guard torna isto um no-op fiel (2092-2103).
 */
function updateInsight(activeFilter: MonitorFilter): void {
  const insight = qs('#u-mon-insight');
  if (!insight) return;
  /* v8 ignore next -- activeFilter sempre chave válida do Record; ?? all defensivo p/ noUncheckedIndexedAccess */
  insight.innerHTML = MONITOR_INSIGHT_BY_FILTER[activeFilter] ?? MONITOR_INSIGHT_BY_FILTER.all;
}

/** Le e valida o filtro de um botao (data-filter), com fallback 'all' (2051). */
function readFilter(btn: Element): MonitorFilter {
  const raw = (btn as HTMLElement).dataset['filter'];
  const valid: readonly MonitorFilter[] = ['all', 'crit', 'warn', 'sec', 'event', 'poll'];
  return (valid as readonly string[]).includes(raw ?? '') ? (raw as MonitorFilter) : 'all';
}

/** Instancia unica do controlador (espelha o singleton Sections.monitoramento). */
export const monitoramentoController = new MonitoramentoCommandController();
